
interface SidebarInterface {
  activityDefinitions: ActivityDefinition[];
  activityEventSortDirection: string;
  combinedEvents: ActivityEvent[];
  commitJobActivityDefinitions: any;
  errorsOnlyFilter: boolean;
  etlPanels: number[];
  getDateAdjustedForUTC(date?: Date | string | null): Date;
  getJobPassIconColor: string;
  getPhaseAndDefinitionEvents: any;
  getReplayedEventsObjectStatus: any;
  getReplayRequests: any;
  getReplays: any;
  listMode: boolean;
  loadingPhaseAndDefEvents: boolean;
  phaseTypePanels: [number[], number[], number[]];
  profile: IdTokenClaims;
  refreshListMode: any;
  replayRequestsLoaded: boolean;
  replayRequestService: ReplayRequestService;
  selectEvent: any;
  scroll: boolean;
  scrollToEvent: any;
  selectedDefinition: ActivityDefinition;
  selectedEvent: ActivityEvent | null;
  selectedJob: Job[];
}

import { AxiosResponse } from 'axios';
import ActivityDefinition from '@/models/activityDefinition';
import ActivityEvent from '@/models/activityEvent';
import { bus } from '@/main';
import { IdTokenClaims } from '@/auth/idTokenClaims';
import Job from '@/models/job';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PipelinePhaseType, ActivityEventType, ReplayRequestType, PipelineStatus, ActivityDefinitionIds } from '@/models/enums';
import ReplayedEvents from '@/models/replayedEvents';
import ReplayRequest from '@/models/replayRequest';
import ReplayRequestService from '@/services/replayRequestService';
import Vue, { PropType } from 'vue';
import { VueConstructor } from 'vue/types/vue';

const ModelPublishSidebar = (Vue as VueConstructor<Vue & SidebarInterface>).extend({
  props: {
    selectedJob: {
      type: Array,
      required: true,
    },
    selectedEvent: {
      type: Object,
      required: false,
    },
    replayJobIds: {
      type: Array as PropType<number[]>,
      required: false,
    },
  },
  watch: {
    selectedJob: {
      handler(newVal, oldVal) {
        if (oldVal[0].id !== newVal[0].id) {
          this.etlPanels = [0, 1, 2];
          this.phaseTypePanels = [[], [], []];
          this.getReplayRequests();
        } else {
          if (newVal[0].replayRequests !== oldVal[0].replayRequests) {
            this.$emit('events-loaded', false);
            this.$emit('events-loaded', true);
          }
        }
        if (this.listMode) this.refreshListMode(true);
      },
      deep: true,
    },

    selectedEvent() {
      if (this.selectedEvent !== null) {
        if (!this.listMode && this.selectedEvent.activityDefinition) {
          this.phaseTypePanels = [[], [], []];
          const phaseType = this.selectedEvent.activityDefinition.pipelinePhaseTypeId as number;
          const defs = this.getPhaseDefinitions(phaseType, this.errorsOnlyFilter, this.selectedJob[0]);
          const panel = this.phaseTypePanels[phaseType - 1];
          const index = defs.indexOf(this.selectedEvent.activityDefinition);
          panel.push(index);
        }
        if (this.scroll) {
          this.scrollToEvent();
        }
      }
    },
    replayJobIds() {
      this.getReplayRequests();
    },
  },

  computed: {
    ...mapState<ActivityDefinition[]>('activityDefinitionStore', {
      activityDefinitions: 'jobActivityDefinitions',
    }),
    ...mapState('userStore', {
      profile: 'profile',
    }),
    ...mapGetters('activityDefinitionStore', {
      getPhaseDefinitions: 'getPhaseDefinitions',
    }),
    modelName() {
      let job = this.selectedJob[0];
      return job && job.parsedMetadata ? job.parsedMetadata.ModelName : null;
    },
  },

  created() {
    this.etlPanels = [0, 1, 2];
    this.phaseTypePanels = [[], [], []];
    if (this.$route.params.jobId) {
      if (this.selectedJob.length === 0) {
        this.etlPanels = [0, 1, 2];
      }
      if (this.$route.params.activityEventId) {
        this.scroll = true;
      }
    }

    let aeSortDirKey = 'model-pipeline' + '-activity-event-sort-direction';
    this.activityEventSortDirection = localStorage.getItem(aeSortDirKey) || 'asc';

    this.replayRequestService = new ReplayRequestService();
    this.getReplayRequests();
  },

  data() {
    return {
      activityEventSortDirection: 'asc' as string,
      ActivityEventType: ActivityEventType,
      ActivityDefinitionIds: ActivityDefinitionIds,
      combinedEvents: [] as ActivityEvent[],
      errorsOnlyFilter: false,
      etlPanels: [] as number[],
      listMode: false,
      loadingPhaseAndDefEvents: false,
      pipelinePhaseTypes: [PipelinePhaseType.Extract, PipelinePhaseType.Transform, PipelinePhaseType.Load],
      phaseTypePanels: [[] as number[], [] as number[], [] as number[]],
      replayRequestsLoaded: false,
      scroll: false,
      selectedDefinition: null as ActivityDefinition | null,
    };
  },

  methods: {
    ...mapActions('activityDefinitionStore', ['commitJobActivityDefinitions']),

    displayEventDT(event: ActivityEvent) {
      if (!event.eventDateTime) return 'N/A';
      const date = this.getDateAdjustedForUTC(event.eventDateTime);
      return date.toLocaleTimeString();
    },

    expandDefinition(phaseType: number, ad: ActivityDefinition) {
      // TODO: this only needs to be called on expanding the panel, NOT when collapsing the panel.
      this.selectedDefinition = ad;
      this.getPhaseAndDefinitionEvents(ad, 1);
    },

    getDateAdjustedForUTC(date?: Date | string | null) {
      const dateNoZ = date?.toString().indexOf('Z') === -1;
      return date ? new Date(date + (dateNoZ ? 'Z' : '')) : new Date();
    },

    getReplays(ad: ActivityDefinition) {
      const job = this.selectedJob[0];
      let collection = ad.events.filter(
        (e) => e.pipelinePhaseTypeId === ad.pipelinePhaseTypeId && e.activityDefinitionId === ad.id,
      );
      const replayedEventObjects = [] as ReplayedEvents[];
      if (!job.replayRequests) return replayedEventObjects;
      const replays = job.replayRequests.filter((x) => x.activityDefinitionId === ad.id);
      if (replays.length > 0) {
        // Build first run
        const firstRun = new ReplayedEvents();
        firstRun.name = 'First Run';
        firstRun.events = collection.filter((x) => !x.replayId);
        firstRun.status = this.getReplayedEventsObjectStatus(firstRun.events);
        firstRun.hasWarnings = firstRun.events.some((x) => x.warningCount > 0);
        firstRun.hasErrors = firstRun.events.some((x) => x.exceptionCount > 0);
        replayedEventObjects.push(firstRun);
        // Build subsequent replays
        replays.forEach((replay, index) => {
          const replayedEventsObject = new ReplayedEvents();
          replayedEventsObject.name =
            'Replay ' + (index + 1) + (replay.requestedByUserId ? ' [' + replay.requestedByUserId + ']' : '');
          replayedEventsObject.events = collection.filter((event) => event.replayId === replay.id);
          replayedEventsObject.status = replay.status;
          replayedEventsObject.hasWarnings = replayedEventsObject.events.some((x) => x.warningCount > 0);
          replayedEventsObject.hasErrors = replayedEventsObject.events.some((x) => x.exceptionCount > 0);
          replayedEventObjects.push(replayedEventsObject);
        });
      }
      ad.replays = replayedEventObjects;
    },

    getReplayedEventsObjectStatus(events: ActivityEvent[]) {
      if (events.length > 0) {
        return this.getEventsStatus(events);
      } else {
        return PipelineStatus.Pending;
      }
    },

    getReplayRequests() {
      this.replayRequestsLoaded = false;
      const jobId = this.selectedJob[0].id;
      this.replayRequestService.getReplayRequests(this.$http, jobId).then((result: ReplayRequest[]) => {
        const replayRequests = result;
        this.replayRequestsLoaded = true;
        this.$emit('update-replay-requests', replayRequests);
      });
    },

    disableErrorButtons() {
      return (
        this.selectedJob[0].extractExceptions === 0 &&
        this.selectedJob[0].transformExceptions === 0 &&
        this.selectedJob[0].loadExceptions === 0
      );
    },

    expandedActivityDefinitionPanel(adId: number) {
      let ad = this.activityDefinitions.find((ad) => ad.id === adId);
      if (ad) ad.isExpanded = !ad.isExpanded;
    },

    getPipelinePhaseName(id: number) {
      return PipelinePhaseType[id];
    },

    getPhaseAndDefinitionEvents(ad: ActivityDefinition, page: number | null = null): void {
      let query = 'activityDefinitionId=' + ad.id + '&jobIds=' + this.selectedJob[0].id;
      if (page) query += '&page=' + page;
      query += '&sortDesc=' + (this.activityEventSortDirection === 'desc');
      this.loadingPhaseAndDefEvents = true;
      this.$http.get<never>('activityEvent?' + query).then((result: AxiosResponse<ActivityEvent[]>) => {
        // Create a map of event ids that have already been loaded so new events can be quickly filtered for duplicates
        let ids = ad.events.map((e) => e.id).reduce((map: Record<string, boolean>, id) => ((map[id] = true), map), {});
        ad.events = ad.events.concat(result.data.filter((d) => !ids[d.id]));
        this.commitJobActivityDefinitions([ad]);
        this.getReplays(ad);
        this.loadingPhaseAndDefEvents = false;
      });
    },

    getEventActivityDefinitionName(event: ActivityEvent) {
      const ad = this.activityDefinitions.find((ad) => ad.id === event?.activityDefinitionId);
      return ad?.name || 'N/A';
    },

    getEventType(typeId: number) {
      return ActivityEventType[typeId];
    },

    getMaxTelemetryTime(event: ActivityEvent) {
      if (!event.telemetryItems || event.telemetryItems.length === 0) return null;
      const lastTelemetry = event.telemetryItems[event.telemetryItems.length - 1];
      const date = this.getDateAdjustedForUTC(lastTelemetry.createDateTime);
      return date.toLocaleTimeString();
    },

    getListModeEvents() {
      if (this.errorsOnlyFilter) {
        return this.combinedEvents.filter((x) => x.exceptionCount > 0 || !x.isSuccessful);
      } else {
        return this.combinedEvents;
      }
    },

    loadMoreEvents(ad: ActivityDefinition) {
      let page = Math.floor(ad.events.length / 500) + 1;
      this.getPhaseAndDefinitionEvents(ad, page);
    },

    refreshListMode(clearEvents?: boolean) {
      let query = 'jobIds=' + this.selectedJob[0].id + '&page=' + 1;
      this.$http.get<never>('activityEvent?' + query).then((result: AxiosResponse<ActivityEvent[]>) => {
        if (clearEvents) this.combinedEvents = [];
        // Create a map of event ids that have already been loaded so new events can be quickly filtered for duplicates
        let ids = this.combinedEvents
          .map((e) => e.id)
          .reduce((map: Record<string, boolean>, id) => ((map[id] = true), map), {});
        this.combinedEvents = this.combinedEvents.concat(result.data.filter((d) => !ids[d.id]));
      });
    },

    selectFirstError() {
      const errorEvents = this.combinedEvents.filter((x) => !x.isSuccessful || x.exceptionCount > 0);
      this.selectEvent(errorEvents[0], true);
    },

    selectPreviousError() {
      if (this.selectedEvent !== null && this.selectedEvent.isSuccessful) {
        const allEvents = this.combinedEvents as ActivityEvent[];
        const index = allEvents.indexOf(this.selectedEvent);
        const previousErrors = allEvents.filter(
          (x) => allEvents.indexOf(x) < index && (!x.isSuccessful || x.exceptionCount > 0),
        );
        if (previousErrors.length > 0) {
          this.selectEvent(previousErrors[previousErrors.length - 1], true);
        }
      } else {
        const errorEvents = this.combinedEvents.filter((x) => !x.isSuccessful || x.exceptionCount > 0);
        const index = errorEvents.indexOf(this.selectedEvent as ActivityEvent);
        if (index > 0) {
          this.selectEvent(errorEvents[index - 1], true);
        }
      }
    },

    selectNextError() {
      if (this.selectedEvent !== null && this.selectedEvent.isSuccessful) {
        const allEvents = this.combinedEvents as ActivityEvent[];
        const index = allEvents.indexOf(this.selectedEvent);
        const nextEvent = allEvents.find(
          (x) => allEvents.indexOf(x) > index && (!x.isSuccessful || x.exceptionCount > 0),
        );
        if (nextEvent !== undefined) {
          this.selectEvent(nextEvent, true);
        }
      } else {
        const errorEvents = this.combinedEvents.filter((x) => !x.isSuccessful || x.exceptionCount > 0);
        const index = errorEvents.indexOf(this.selectedEvent as ActivityEvent);
        if (index < errorEvents.length - 1) {
          this.selectEvent(errorEvents[index + 1], true);
        }
      }
    },

    selectLastError() {
      const errorEvents = this.combinedEvents.filter((x) => !x.isSuccessful || x.exceptionCount > 0);
      this.selectEvent(errorEvents[errorEvents.length - 1], true);
    },

    selectEvent(event: ActivityEvent, scroll: boolean) {
      this.$emit('select-event', event);
      this.scroll = scroll;
    },

    scrollToEvent() {
      if (this.selectedEvent) {
        const prefix = this.listMode ? 'list' : 'tree';
        const ref = this.$refs[prefix + this.selectedEvent.id] as Element[];
        if (ref) {
          ref[0].scrollIntoView({
            block: 'start',
            inline: 'start',
          });
        }
      }
    },

    showEllipses(ad: ActivityDefinition, index: number): boolean {
      const panel = this.phaseTypePanels[(ad.pipelinePhaseTypeId as number) - 1];
      let show = false;
      if (panel.indexOf(index) > -1) {
        show = true;
      }
      return show;
    },

    showEventEllipses(ad: ActivityDefinition, event: ActivityEvent) {
      return ad.isReplayable && ad.eventsReplayable && event.activityEventTypeId === ActivityEventType.Started;
    },

    sendReplayRequest(jobId: number, adId: number, isolated: boolean, eventId?: number) {
      const replayRequest = new ReplayRequest(
        jobId,
        adId,
        eventId ? ReplayRequestType.IsolatedEvent : isolated ? ReplayRequestType.Isolated : ReplayRequestType.Flow,
        this.profile.name,
        new Date(),
        eventId,
      );

      this.$http
        .post('replayRequest', replayRequest)
        .then(() => {
          this.getReplayRequests();
          bus.$emit('alert', 'Replay Request Sent', 'success', 6000);
        })
        .catch((error) => {
          bus.$emit('alert', 'Replay Request failed ', 'error', 6000, error);
        });
    },

    sendReplayRequestWithCompletedReferences(jobId: number, adId: number, isolated: boolean, eventId?: number) {
      const replayRequest = new ReplayRequest(
        jobId,
        adId,
        eventId ? ReplayRequestType.IsolatedEvent : isolated ? ReplayRequestType.Isolated : ReplayRequestType.Flow,
        this.profile.name,
        new Date(),
        eventId,
        undefined,
        true, // useCompletedReferences = true
      );

      this.$http
        .post('replayRequest', replayRequest)
        .then(() => {
          this.getReplayRequests();
          bus.$emit('alert', 'Replay Request with Completed References Sent', 'success', 6000);
        })
        .catch((error) => {
          bus.$emit('alert', 'Replay Request with Completed References failed ', 'error', 6000, error);
        });
    },

    toggleActivityEventSort() {
      this.activityEventSortDirection = this.activityEventSortDirection === 'asc' ? 'desc' : 'asc';
      let aeSortDirKey = 'model-pipeline' + '-activity-event-sort-direction';
      localStorage.setItem(aeSortDirKey, this.activityEventSortDirection);
      const expanded = this.activityDefinitions.filter((ad) => ad.isExpanded);
      for (let i = 0; i < expanded.length; i++) {
        expanded[i].events = [];
        this.getPhaseAndDefinitionEvents(expanded[i], 1);
      }
    },

    toggleErrorsOnlyFilter() {
      this.errorsOnlyFilter = !this.errorsOnlyFilter;
      if (this.errorsOnlyFilter) {
        this.listMode = true;
        this.refreshListMode(true);
      }
    },

    toggleListMode() {
      this.listMode = !this.listMode;
      if (!this.listMode) this.errorsOnlyFilter = false;
      if (this.listMode) this.refreshListMode(true);
    },
  },
});

export default ModelPublishSidebar;
